import { readonly, ref, type Ref } from "vue";

/**
 * Shared "lay this out for a phone" flag: below Tailwind's `sm`, or a phone turned on its side.
 *
 * Module-scope state and a single MediaQueryList listener, in the style of the Pinia stores here
 * (see stores/Permissions.ts): every consumer reads the same ref rather than each mounting its own
 * matchMedia. Several surfaces need to make a *structural* choice at this breakpoint rather than a
 * purely visual one — rendering a different control, or turning virtual scrolling off — and those
 * cannot be expressed as a utility.
 *
 * Reach for the `compact:` variant first (index.css; `wide:` is its complement). This is only for
 * cases where the DOM itself has to differ.
 */

/**
 * UX roadmap #29: a phone on its side is 568-932px wide, so a width breakpoint alone gave it the desktop tables (in a
 * 65vh scroller of about 250px) and tab rows, inside the phone shell. A touch screen at most 500px tall is a phone in
 * landscape; tablets are taller either way up, and a desktop window with a mouse is never matched by height. Below lg
 * only: that is where the phone shell is, and it keeps a landscape tablet whose browser shrinks the layout viewport for
 * the keyboard from switching layouts while a field has focus.
 *
 * The same query as the `compact` variant in index.css and the plain media rules that follow it; keep them in step.
 */
export const COMPACT_VIEWPORT_QUERY = "(max-width: 639px), (pointer: coarse) and (max-height: 500px) and (max-width: 1023px)";

function initialMatch(): boolean {
    if ( typeof window === "undefined" || typeof window.matchMedia !== "function" ) {
        // happy-dom in unit tests, and any non-browser context. Defaulting to false preserves the
        // desktop rendering that existing specs were written against.
        return false;
    }
    return window.matchMedia( COMPACT_VIEWPORT_QUERY ).matches;
}

const isCompact = ref< boolean >( initialMatch() );

let mediaQuery: MediaQueryList | null = null;

function ensureListening(): void {
    if ( mediaQuery ) return;
    if ( typeof window === "undefined" || typeof window.matchMedia !== "function" ) return;

    mediaQuery = window.matchMedia( COMPACT_VIEWPORT_QUERY );
    isCompact.value = mediaQuery.matches;

    // Never torn down: the listener is one per document for the lifetime of the app, and removing
    // it when the last consumer unmounts would leave the next mount with a stale value until the
    // following resize. It also fires when a phone is turned, which a width-only query did not.
    mediaQuery.addEventListener( "change", ( event: MediaQueryListEvent ) => {
        isCompact.value = event.matches;
    } );
}

export function useCompactViewport(): { isCompactViewport: Readonly< Ref< boolean > > } {
    ensureListening();
    return { isCompactViewport: readonly( isCompact ) };
}

/** Test seam: force a value and stop tracking the real viewport. */
export function __setCompactViewportForTests( value: boolean ): void {
    isCompact.value = value;
}
